/**
 * AdminChangePassword.js — first-run password rotation gate (2026-05-10).
 *
 * Renders when a seed-admin (or any admin with `must_change_password=true`
 * on their user doc) tries to access /admin/*. ProtectedRoute redirects
 * them here and locks every other admin route until they POST a new
 * strong password.
 *
 * Strong-password rules mirror backend `validate_strong_password`:
 *   • minimum 10 characters
 *   • at least one uppercase, one lowercase, one digit, one special
 *
 * Mobile-first; supports EN + AR (RTL).  No other auth flow change.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, AlertTriangle, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function validate(pwd) {
  return {
    length:    pwd.length >= 10,
    upper:     /[A-Z]/.test(pwd),
    lower:     /[a-z]/.test(pwd),
    digit:     /\d/.test(pwd),
    special:   /[^A-Za-z0-9]/.test(pwd),
  };
}

export default function AdminChangePassword() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { token, user, refreshUser } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  const rules = useMemo(() => validate(newPwd), [newPwd]);
  const allRulesPass = Object.values(rules).every(Boolean);
  const confirmMatches = newPwd.length > 0 && newPwd === confirmPwd;

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!allRulesPass) {
      setErr(isRTL ? 'كلمة السر لا تستوفي الشروط' : 'Password does not meet the rules');
      return;
    }
    if (!confirmMatches) {
      setErr(isRTL ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/me/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });
      const data = await r.json();
      if (!r.ok) {
        setErr(data.detail || (isRTL ? 'فشل تغيير كلمة السر' : 'Failed to change password'));
        return;
      }
      setOk(true);
      // Refresh user so must_change_password flips to false, then unlock the dashboard.
      try { await refreshUser?.(); } catch { /* noop */ }
      setTimeout(() => navigate('/admin/moderation', { replace: true }), 900);
    } catch (e2) {
      setErr(String(e2?.message || e2));
    } finally {
      setBusy(false);
    }
  }

  const rule = (passed, label) => (
    <div className={`acp-rule ${passed ? 'acp-rule--ok' : ''}`}>
      {passed ? <Check size={14} /> : <X size={14} />}
      <span>{label}</span>
    </div>
  );

  return (
    <div className="acp-wrap" dir={isRTL ? 'rtl' : 'ltr'} data-testid="admin-change-password-page">
      <div className="acp-card">
        <div className="acp-header">
          <div className="acp-icon"><ShieldCheck size={26} /></div>
          <div>
            <h1 className="acp-title">
              {isRTL ? 'تغيير كلمة السر مطلوب' : 'Password change required'}
            </h1>
            <p className="acp-sub">
              {isRTL
                ? 'يستخدم هذا الحساب كلمة السر الافتراضية للتطوير. يرجى تعيين كلمة سر قوية قبل الوصول إلى لوحة الإدارة.'
                : 'This account is using the development seed password. Please set a strong password before accessing the admin dashboard.'}
            </p>
          </div>
        </div>

        {user?.is_seed_admin && (
          <div className="acp-dev-banner" data-testid="acp-dev-badge">
            <AlertTriangle size={14} />
            <span>{isRTL ? 'حساب تطوير — لا تستخدمه في الإنتاج' : 'Development admin account — do not use in production'}</span>
          </div>
        )}

        <form className="acp-form" onSubmit={handleSubmit}>
          <label className="acp-label">
            {isRTL ? 'كلمة السر الحالية' : 'Current password'}
          </label>
          <input
            type="password" className="acp-input" required value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
            autoComplete="current-password"
            data-testid="acp-old-password"
          />

          <label className="acp-label">
            {isRTL ? 'كلمة السر الجديدة' : 'New password'}
          </label>
          <input
            type="password" className="acp-input" required value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            autoComplete="new-password"
            data-testid="acp-new-password"
          />

          <div className="acp-rules" data-testid="acp-rules">
            {rule(rules.length,  isRTL ? '١٠ خانات على الأقل'      : 'At least 10 characters')}
            {rule(rules.upper,   isRTL ? 'حرف كبير واحد على الأقل' : 'At least one uppercase letter')}
            {rule(rules.lower,   isRTL ? 'حرف صغير واحد على الأقل' : 'At least one lowercase letter')}
            {rule(rules.digit,   isRTL ? 'رقم واحد على الأقل'       : 'At least one number')}
            {rule(rules.special, isRTL ? 'رمز خاص واحد على الأقل'  : 'At least one special character')}
          </div>

          <label className="acp-label">
            {isRTL ? 'تأكيد كلمة السر' : 'Confirm new password'}
          </label>
          <input
            type="password" className="acp-input" required value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            autoComplete="new-password"
            data-testid="acp-confirm-password"
          />

          {err && <div className="acp-error" data-testid="acp-error">{err}</div>}
          {ok && (
            <div className="acp-ok" data-testid="acp-success">
              {isRTL ? 'تم تغيير كلمة السر بنجاح…' : 'Password changed — unlocking dashboard…'}
            </div>
          )}

          <button
            type="submit"
            className="acp-submit"
            disabled={busy || !allRulesPass || !confirmMatches || !oldPwd}
            data-testid="acp-submit"
          >
            {busy
              ? (isRTL ? '…جارٍ التغيير' : 'Changing…')
              : (isRTL ? 'تغيير كلمة السر' : 'Change password')}
          </button>
        </form>
      </div>

      <style>{`
        .acp-wrap { min-height: 100vh; padding: 24px 16px; background: #0f172a; color: #f1f5f9; display: flex; align-items: center; justify-content: center; }
        .acp-card { width: 100%; max-width: 460px; background: #1e293b; border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; padding: 22px; box-shadow: 0 24px 60px rgba(0,0,0,0.5); }
        .acp-header { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 14px; }
        .acp-icon { background: rgba(56,189,248,0.12); color: #38bdf8; padding: 10px; border-radius: 10px; flex-shrink: 0; }
        .acp-title { font-size: 18px; font-weight: 700; margin: 0 0 4px 0; }
        .acp-sub { font-size: 13px; color: #94a3b8; line-height: 1.5; margin: 0; }
        .acp-dev-banner { display: flex; gap: 8px; align-items: center; background: #422006; border: 1px solid #b45309; color: #fcd34d; padding: 8px 12px; border-radius: 10px; font-size: 12px; margin-bottom: 16px; }
        .acp-form { display: flex; flex-direction: column; gap: 6px; }
        .acp-label { font-size: 12px; color: #cbd5e1; margin-top: 10px; }
        .acp-input { padding: 10px 12px; background: #0f172a; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; color: #f1f5f9; font-size: 14px; }
        .acp-input:focus { outline: 2px solid #38bdf8; }
        .acp-rules { display: flex; flex-direction: column; gap: 4px; background: rgba(255,255,255,0.03); border-radius: 8px; padding: 10px 12px; margin: 8px 0 6px; font-size: 12px; color: #94a3b8; }
        .acp-rule { display: flex; gap: 6px; align-items: center; }
        .acp-rule--ok { color: #4ade80; }
        .acp-rule svg { flex-shrink: 0; }
        .acp-error { background: #450a0a; border: 1px solid #b91c1c; color: #fecaca; padding: 8px 12px; border-radius: 8px; font-size: 12px; margin-top: 8px; }
        .acp-ok { background: #052e16; border: 1px solid #16a34a; color: #bbf7d0; padding: 8px 12px; border-radius: 8px; font-size: 12px; margin-top: 8px; }
        .acp-submit { margin-top: 16px; padding: 12px 16px; background: #38bdf8; color: #0f172a; border: none; border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer; }
        .acp-submit:disabled { opacity: 0.45; cursor: not-allowed; }
        [dir="rtl"] .acp-rule { flex-direction: row-reverse; }
        [dir="rtl"] .acp-rules { text-align: right; }
      `}</style>
    </div>
  );
}
